import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiDelete } from "../../../api"; // Assuming you have apiDelete
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiChevronDown,
  FiChevronRight,
  FiTrash2,
} from "react-icons/fi";
import "../styles/MySchedule.css"; // Import the new CSS file

const TABS = ["All", "F", "W", "S", "Y"];
const termLabel = (t) => {
  switch (t) {
    case "F":
      return "Fall";
    case "W":
      return "Winter";
    case "S":
      return "Summer";
    case "Y":
      return "Year-long";
    default:
      return "All";
  }
};
const typeRank = (t) =>
  t === "LECT" ? 0 : t === "TUT" ? 1 : t === "LAB" ? 2 : 3;

export default function MySchedule() {
  const nav = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [tab, setTab] = useState("All");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    apiGet("/schedules")
      .then((r) => setSchedules(Array.isArray(r.schedules) ? r.schedules : []))
      .catch(() => setSchedules([]));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    if (tab === "All") {
      return schedules;
    } else if (tab === "F") {
      // Show Fall courses AND Year-long courses
      return schedules.filter((s) => s.term === "F" || s.term === "Y");
    } else if (tab === "W") {
      // Show Winter courses AND Year-long courses
      return schedules.filter((s) => s.term === "W" || s.term === "Y");
    } else {
      return schedules.filter((s) => s.term === tab);
    }
  }, [schedules, tab]);

  const byTerm = useMemo(() => {
    const map = new Map();
    filtered.forEach((row) => {
      if (!map.has(row.term)) map.set(row.term, new Map());
      const courses = map.get(row.term);
      if (!courses.has(row.courseCode)) courses.set(row.courseCode, []);
      courses.get(row.courseCode).push(row);
    });
    map.forEach((courses) => {
      courses.forEach((rows, code) => {
        rows.sort(
          (a, b) =>
            typeRank(a.type) - typeRank(b.type) ||
            (a.startTime || "").localeCompare(b.startTime || "")
        );
        courses.set(code, rows);
      });
    });
    return map;
  }, [filtered]);

  const doDelete = async (code) => {
    try {
      await apiDelete(`/schedules/code/${encodeURIComponent(code)}`);
      setConfirmDelete(null);
      await load();
    } catch {
      setConfirmDelete(null);
    }
  };

  const toggleExpand = (code) =>
    setExpanded((prev) => ({ ...prev, [code]: !prev[code] }));
  const expandAll = () =>
    setExpanded(
      Array.from(byTerm.values())
        .flatMap((courses) => Array.from(courses.keys()))
        .reduce((acc, code) => ({ ...acc, [code]: true }), {})
    );
  const collapseAll = () => setExpanded({});

  const hasAny = schedules.length > 0;
  const termKeys = Array.from(byTerm.keys()).sort(
    (a, b) => "FWS".indexOf(a) - "FWS".indexOf(b)
  );

  return (
    <div className="schedule-container page-card">
      {/* UPDATED: Term tabs are now in the header */}
      <div className="schedule-header">
        <h2>My Schedule</h2>
        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`tab-btn ${tab === t ? "active" : ""}`}
            >
              {termLabel(t)}
            </button>
          ))}
        </div>
      </div>

      {/* UPDATED: "Select Courses" button is now here */}
      <div className="schedule-controls">
        <button
          className="btn btn-primary"
          onClick={() => nav("/app/react/select-courses")}
        >
          <FiPlus />
          Select Courses
        </button>
        {hasAny && (
          <div className="expand-controls">
            <button className="btn btn-secondary" onClick={expandAll}>
              Expand All
            </button>
            <button className="btn btn-secondary" onClick={collapseAll}>
              Collapse All
            </button>
          </div>
        )}
      </div>

      {!hasAny ? (
        <p className="no-schedule-msg">
          Your schedule is empty. Add some courses to get started!
        </p>
      ) : (
        termKeys.map((termKey) => (
          <div key={termKey} className="term-section">
            {tab === "All" && (
              <h3 className="term-title">{termLabel(termKey)}</h3>
            )}
            {Array.from(byTerm.get(termKey).keys()).map((code) => {
              const rows = byTerm.get(termKey).get(code);
              const lect = rows.find((r) => r.type === "LECT") || rows[0];
              return (
                <div key={code} className="course-card">
                  <div
                    className="course-header"
                    onClick={() => toggleExpand(code)}
                  >
                    <div className="course-title">
                      {expanded[code] ? <FiChevronDown /> : <FiChevronRight />}
                      <strong>{`${code} - ${lect?.section ?? ""}`}</strong>
                    </div>
                    {confirmDelete === code ? (
                      <div className="confirm-delete">
                        <span>Delete?</span>
                        <button
                          className="btn btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            doDelete(code);
                          }}
                        >
                          Yes
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(null);
                          }}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(code);
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                  {expanded[code] && (
                    <div className="course-details">
                      <table>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Section</th>
                            <th>Days</th>
                            <th>Start</th>
                            <th>Duration</th>
                            <th>Room</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((s) => (
                            <tr key={s.id}>
                              <td>{s.type}</td>
                              <td>{s.section}</td>
                              <td>{s.days}</td>
                              <td>{s.startTime}</td>
                              <td>{s.duration ?? ""}</td>
                              <td>{s.room}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
